<script setup lang="ts">
import { FieldItem } from '@/coreDomainsModule/constant/validateRules';
import InfoModal from '@/corePagesModule/components/InfoModal.vue';
import { useForms } from '@/corePagesModule/composables/useForm';
import SingleSelectInput from '@/corePagesModule/SelectInput/SingleSelectInput.vue';
import {
  AGGREGATE_BUSINESS_DIVIDE_COLUMN,
  formOptionsAggregateBusiness,
  formOptionsAggregateBusinessUniqueKeys,
  formOptionsOrder,
  inputTypeKeyMap,
  keyMappingForSelectInput,
  unitOptions,
  validateRuleKeyMap,
} from '@/corePagesModule/updateBusinessModal/constants';
import { i18n } from '@/i18n';
import { computed, ref, watch } from 'vue';
import { useUpdateFieldState } from '@/corePagesModule/updateBusinessModal/composables/useUpdateFieldState';
import { OrderBusinessForRegister, UnmatchedOrderItem } from '@/unmatchedOrderModule/domains/entities/UnmatchedOrderItem';
import TextInput from '@/unmanagedBusinessModule/components/TextInput.vue';
import MultipleUserSelectInput from '@/corePagesModule/user/SelectInput/MultipleUserSelectInput.vue';
import { LottieAnimation } from 'lottie-web-vue';
import loadingAnimation from '@/assets/loadingAnimation.json';
import AddOptionSection from '@/corePagesModule/updateBusinessModal/components/AddOptionSection.vue';
import { UpdateBusinessViewModel } from '@/corePagesModule/updateBusinessModal/UpdateBusinessViewModel';
import { diContainer } from '@/coreContainer/container';
import DateInputWithLabel from './DateInputWithLabel.vue';
import { BusinessFactory } from '@/coreDomainsModule/factories/BusinessFactory';
import { showInfoModal } from '@/corePagesModule/components/InfoModal/InfoModalManager';
import { UserAccountFactory } from '@/coreDomainsModule/factories/userAccountFactory';
import { convertLeadTimeToDays } from '@/coreDomainsModule/leadtime';
import { DateService } from '@/coreDomainsModule/services/dateService';
import { OrderStatus, stringToOrderStatusSafe } from '@/coreDomainsModule/entities/arrival';
import OrderStatusFormSelectInput from './OrderStatusFormSelectInput.vue';

const t = i18n.t;
const updateBusinessVM = new UpdateBusinessViewModel();

const props = withDefaults(defineProps<{ modalState: boolean; item?: UnmatchedOrderItem }>(), {
  item: undefined,
});
const emits = defineEmits<{
  (e: 'close', isNext?: boolean, info?: OrderBusinessForRegister): void;
}>();

const isLoading = ref(false);
const withAdditionalInfo = ref(false);
const currentOpenSelectInputName = ref('');

const targetArray = computed(() =>
  withAdditionalInfo.value
    ? [...formOptionsAggregateBusiness, ...formOptionsOrder]
    : [...formOptionsAggregateBusinessUniqueKeys, ...formOptionsOrder]
);

const fieldItems = computed((): FieldItem[] => {
  const items = targetArray.value.map((name: string) => {
    return {
      name,
      rules: validateRuleKeyMap[name],
    };
  });
  return items;
});

const { fieldState, allValidate, resetForm } = useForms(fieldItems.value);
const { addNewOption, updateValue, updateUserValue } = useUpdateFieldState(fieldState);

/**
 * フォームのリストを見た目上で分かれている部分で分割して取得する
 */
const getDividedFormOptions = computed(() => {
  const dividePoint = formOptionsAggregateBusiness.indexOf(AGGREGATE_BUSINESS_DIVIDE_COLUMN);
  return [formOptionsAggregateBusiness.slice(0, dividePoint), formOptionsAggregateBusiness.slice(dividePoint)];
});

const addableOptions = computed(() => {
  return targetArray.value
    .filter((option) => {
      return (
        inputTypeKeyMap[option] === 'select' && (!withAdditionalInfo.value || !formOptionsAggregateBusinessUniqueKeys.includes(option))
      );
    })
    .map((r) => t(r));
});

const modalDesc = computed(() =>
  withAdditionalInfo.value ? t('当該の集計が存在しないため、集計も同時に作成します。') : t('新規に発注を登録します。')
);

const orderStatusModelValue = computed(() => stringToOrderStatusSafe(fieldState.orderStatus.value.value ?? ''));

/**
 * inputのgrid幅指定クラス取得
 * @param option - inputの対応名
 */
const getBaseDataItemClass = (option: string) => {
  if (option === 'package' || option === 'unit') {
    return 'modal__item--base-data--number';
  } else {
    return 'modal__item--base-data--normal';
  }
};

/**
 * フォームのstateにPropsの値をセットする
 * @param initialValue - 初期値
 */
const setItemValueToForm = (initialValue: Partial<UnmatchedOrderItem>) => {
  resetForm(fieldItems.value);
  if (!props.item) return;
  for (let i = 0; i < fieldItems.value.length; i++) {
    const keyName = fieldItems.value[i].name;
    switch (keyName) {
      case 'warehouseName':
        fieldState[keyName].setValue(initialValue.warehouse ?? props.item.warehouse ?? '', false);
        break;
      default:
        fieldState[keyName].setValue(initialValue[keyName] ?? props.item[keyName] ?? '', false);
    }
  }
};

/**
 * refの値をリセットする
 */
const resetRef = () => {
  isLoading.value = false;
  currentOpenSelectInputName.value = '';
  withAdditionalInfo.value = false;
};

/**
 * 戻る際の処理
 */
const onBack = async () => {
  if (withAdditionalInfo.value) {
    resetRef();
  } else {
    resetRef();
    emits('close', false);
  }
};

/**
 * 次へ進んだ際の処理
 */
const onNext = async () => {
  if (!allValidate()) return;

  if (withAdditionalInfo.value) {
    createDataAndEmit();
    return;
  }

  const onlyUniqueKeyBusiness = BusinessFactory.create({
    warehouseNumber: fieldState['warehouseNumber'].value.value,
    warehouse: fieldState['warehouseName'].value.value,
    supplierNumber: fieldState['supplierNumber'].value.value,
    supplierName: fieldState['supplierName'].value.value,
    businessNumber: fieldState['businessNumber'].value.value,
    businessName: fieldState['businessName'].value.value,
    package: fieldState['package'].value.value,
    leadTime: 0,
    leadTimeUnit: 'day',
    stdInventoryIndices: {
      標準在庫指数: {
        max: 0,
        min: 0,
        option: { unit: 'day' },
      },
    },
    MOQ: 0,
  });

  isLoading.value = true;
  const existsResult = await diContainer.checkDuplicateAggregateBusinessUseCase.execute(onlyUniqueKeyBusiness);
  isLoading.value = false;
  if (existsResult.status.type === 'error') {
    showInfoModal({
      modalTitle: t('エラーが発生しました。'),
      modalDescription: t('発注登録中にエラーが発生しました。再度お試しください。'),
      backButtonLabel: t('OK'),
      isDisabledNextButton: true,
      icon: 'error',
    });
    return;
  }

  const aggregateBusinessId = existsResult.status.data.aggregateBusinessId;
  if (aggregateBusinessId) {
    createDataAndEmit();
  } else {
    withAdditionalInfo.value = true;
  }
};

/**
 * 親に返すOrderBusinessForRegisterのデータを生成する
 */
const createDataAndEmit = () => {
  const aggregateBusinessResponsibleUsersString = fieldState.aggregateBusinessResponsibleUser?.value.value || '';
  const convertedLeadTimeUnit = updateBusinessVM.convertDateUnit(fieldState.leadTimeUnit?.value.value || '');
  const convertedLeadTime = convertLeadTimeToDays(Number(fieldState.leadTime?.value.value) || 0, convertedLeadTimeUnit);
  const createdOrderDateNumber = stringToFiniteNumberOrUndefined(fieldState.createdOrderDate.value.value) || undefined;
  const desiredDeliveryDateNumber = stringToFiniteNumberOrUndefined(fieldState.desiredDeliveryDate.value.value) || undefined;
  const estimatedTimeOfArrivalNumber = stringToFiniteNumberOrUndefined(fieldState.estimatedTimeOfArrival.value.value) || undefined;
  if (!desiredDeliveryDateNumber || !estimatedTimeOfArrivalNumber) {
    showInfoModal({
      modalTitle: t('エラーが発生しました。'),
      modalDescription: t('発注登録中にエラーが発生しました。再度お試しください。'),
      backButtonLabel: t('OK'),
      isDisabledNextButton: true,
      icon: 'error',
    });
    return;
  }

  const res: OrderBusinessForRegister = {
    // 集計情報
    warehouseNumber: fieldState.warehouseNumber.value.value,
    warehouse: fieldState.warehouseName.value.value,
    supplierNumber: fieldState.supplierNumber.value.value,
    supplierName: fieldState.supplierName.value.value,
    businessNumber: fieldState.businessNumber.value.value,
    businessName: fieldState.businessName.value.value,
    package: fieldState.package.value.value,
    unit: fieldState.unit?.value.value || undefined,
    manufacturer: updateBusinessVM.convertUnassingedToUndefined(fieldState.manufacturer?.value.value) || undefined,
    aggregateBusinessResponsibleUser:
      aggregateBusinessResponsibleUsersString.length > 0
        ? aggregateBusinessResponsibleUsersString.split(',').map((userId) => UserAccountFactory.create({ email: userId }))
        : [],
    userUniqueNumber: fieldState.userUniqueNumber?.value.value || undefined,
    leadTime: convertedLeadTime === -1 ? undefined : convertedLeadTime.toString(),
    leadTimeUnit: convertedLeadTimeUnit,
    stdInventoryIndexMax: fieldState.stdInventoryIndicesMax?.value.value || undefined,
    stdInventoryIndexMin: fieldState.stdInventoryIndicesMin?.value.value || undefined,
    stdInventoryIndexUnit: updateBusinessVM.convertDateUnit(fieldState.stdInventoryIndicesUnit?.value.value || ''),
    moq: fieldState.MOQ?.value.value || undefined,
    orderPackage: fieldState.orderPackage?.value.value || undefined,
    // 発注情報
    orderNumber: fieldState.orderNumber.value.value,
    createdOrderDate: createdOrderDateNumber ? DateService.convertMSUnixTimeToIDate(createdOrderDateNumber) : undefined,
    desiredDeliveryDate: DateService.convertMSUnixTimeToIDate(desiredDeliveryDateNumber),
    estimatedTimeOfArrival: DateService.convertMSUnixTimeToIDate(estimatedTimeOfArrivalNumber),
    orderAmount: fieldState.orderAmount.value.value,
    orderStatus: stringToOrderStatusSafe(fieldState.orderStatus.value.value),
    // 未照合発注情報
    unmatchedOrderId: props.item?.unmatchedOrderId ?? '',
  };
  resetRef();
  emits('close', true, res);
};

/**
 * 値が有限数でない場合はundefinedに変換する
 * @param value - 値
 */
const numberToFiniteOrUndefined = (value: number): number | undefined => {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
};
/**
 * stringの値を有減数に変換する。変換できない場合はundefinedを返す
 * @param value - 値
 */
const stringToFiniteNumberOrUndefined = (value: string): number | undefined => {
  return numberToFiniteOrUndefined(Number(value));
};

watch(
  () => props.modalState,
  async (newVal) => {
    if (newVal) {
      setItemValueToForm({});
    }
  }
);

watch(
  () => withAdditionalInfo.value,
  () => {
    const beforeValue = Object.fromEntries(
      Object.keys(fieldState).map((k) => [k === 'warehouseName' ? 'warehouse' : k, fieldState[k].value.value])
    );
    setItemValueToForm(beforeValue);
  }
);
</script>
<template>
  <InfoModal
    :modal-state="props.modalState"
    :modal-width="1300"
    :back-button-label="withAdditionalInfo ? t('戻る') : t('キャンセル')"
    :next-button-label="t('登録')"
    @on-back="onBack"
    @on-next="onNext"
  >
    <template #title>{{ t('発注新規登録') }}</template>
    <template #description>{{ modalDesc }}</template>
    <template #default>
      <div v-if="isLoading" class="modal__icon">
        <LottieAnimation ref="anim" :animation-data="loadingAnimation" :auto-play="true" :loop="true" :speed="1" />
      </div>
      <template v-else>
        <div class="modal__container modal__container--base-data">
          <template
            v-for="(option, index) in withAdditionalInfo ? getDividedFormOptions[0] : formOptionsAggregateBusinessUniqueKeys"
            :key="option"
          >
            <div class="modal__item" :class="getBaseDataItemClass(option)">
              <template v-if="inputTypeKeyMap[option] === 'select'">
                <SingleSelectInput
                  :index="index"
                  :label="t(option) + updateBusinessVM.getRequiredMark(option)"
                  :model-value="fieldState[option]?.value.value ?? ''"
                  :placeholder="t(option)"
                  :filter-type="keyMappingForSelectInput[option]"
                  :is-error="fieldState[option]?.isError.value ?? false"
                  :error-message="fieldState[option]?.errorMessage.value ?? ''"
                  :disabled="withAdditionalInfo && formOptionsAggregateBusinessUniqueKeys.includes(option)"
                  @click:select-input="() => (currentOpenSelectInputName = option)"
                  @update:model-value="(e) => updateValue(option, e)"
                />
              </template>
              <template v-else-if="inputTypeKeyMap[option] === 'selectUser'">
                <MultipleUserSelectInput
                  :select-input-index="index"
                  :label="t(option)"
                  :placeholder="t(option)"
                  :original-selected-user-id-list="fieldState[option]?.value.value ? fieldState[option].value.value.split(',') : []"
                  :disabled="withAdditionalInfo && formOptionsAggregateBusinessUniqueKeys.includes(option)"
                  @update:model-value="(e) => updateUserValue(option, e)"
                />
              </template>
              <template v-else>
                <TextInput
                  :model-value="fieldState[option]?.value.value ?? ''"
                  :label="t(option) + updateBusinessVM.getRequiredMark(option)"
                  :placeholder="t(option)"
                  :is-error="fieldState[option]?.isError.value ?? false"
                  :error-message="t(fieldState[option]?.errorMessage.value ?? '')"
                  :disabled="withAdditionalInfo && formOptionsAggregateBusinessUniqueKeys.includes(option)"
                  ensure-error-msg-height
                  @update:model-value="(e) => updateValue(option, e)"
                />
              </template>
            </div>
          </template>
        </div>
        <div v-if="withAdditionalInfo" class="modal__container modal__container--amount">
          <template v-for="(option, index) in getDividedFormOptions[1]" :key="option">
            <div class="modal__item">
              <template v-if="inputTypeKeyMap[option] === 'select'">
                <SingleSelectInput
                  :index="index + getDividedFormOptions[0].length"
                  :label="t(option) + updateBusinessVM.getRequiredMark(option)"
                  :model-value="fieldState[option]?.value.value ?? ''"
                  :placeholder="t(option)"
                  :filter-type="keyMappingForSelectInput[option]"
                  :is-error="fieldState[option]?.isError.value ?? false"
                  :error-message="fieldState[option]?.errorMessage.value ?? ''"
                  @click:select-input="() => (currentOpenSelectInputName = option)"
                  @update:model-value="(e) => updateValue(option, e)"
                />
              </template>
              <template v-else-if="inputTypeKeyMap[option] === 'selectUnit'">
                <SingleSelectInput
                  :index="index + getDividedFormOptions[0].length"
                  :label="t(option) + updateBusinessVM.getRequiredMark(option)"
                  :model-value="fieldState[option]?.value.value ?? ''"
                  :placeholder="t(option)"
                  :filter-type="keyMappingForSelectInput[option]"
                  :is-error="fieldState[option]?.isError.value ?? false"
                  :error-message="fieldState[option]?.errorMessage.value"
                  :prop-options="unitOptions.map((o) => t(o))"
                  @click:select-input="() => (currentOpenSelectInputName = option)"
                  @update:model-value="(e) => updateValue(option, e)"
                />
              </template>
              <template v-else>
                <TextInput
                  :model-value="fieldState[option]?.value.value ?? ''"
                  :label="option === 'MOQ' ? t('取引編集MOQ') : t(option) + updateBusinessVM.getRequiredMark(option)"
                  :placeholder="t(option)"
                  :is-error="fieldState[option]?.isError.value ?? false"
                  :error-message="t(fieldState[option]?.errorMessage.value ?? '')"
                  ensure-error-msg-height
                  @update:model-value="(e) => updateValue(option, e)"
                />
              </template>
            </div>
          </template>
        </div>
        <div class="modal__container modal__container--order">
          <template v-for="(option, index) in formOptionsOrder" :key="option">
            <div class="modal__item">
              <template v-if="inputTypeKeyMap[option] === 'date'">
                <DateInputWithLabel
                  :model-value="Number(fieldState[option]?.value.value)"
                  :label="t(option) + updateBusinessVM.getRequiredMark(option)"
                  :placeholder="t(option)"
                  :disabled="withAdditionalInfo"
                  @update:model-value="(e) => updateValue(option, e.toString())"
                />
              </template>
              <template v-else-if="inputTypeKeyMap[option] === 'unique'">
                <template v-if="option === 'orderStatus'">
                  <OrderStatusFormSelectInput
                    :index="index + formOptionsAggregateBusiness.length"
                    :model-value="orderStatusModelValue"
                    :options="[OrderStatus.ApprovedAwaitingDeliveryDate, OrderStatus.Confirmed, OrderStatus.Delivered]"
                    :label="t(option) + updateBusinessVM.getRequiredMark(option)"
                    :disabled="withAdditionalInfo"
                    @update:model-value="(e) => updateValue(option, e)"
                  />
                </template>
              </template>
              <template v-else>
                <TextInput
                  :model-value="fieldState[option]?.value.value"
                  :label="t(option) + updateBusinessVM.getRequiredMark(option)"
                  :placeholder="t(option)"
                  :is-error="fieldState[option]?.isError.value ?? false"
                  :error-message="t(fieldState[option]?.errorMessage.value ?? '')"
                  :disabled="withAdditionalInfo"
                  ensure-error-msg-height
                  @update:model-value="(e) => updateValue(option, e)"
                />
              </template>
            </div>
          </template>
        </div>
        <!-- 新規選択肢追加 -->
        <AddOptionSection :options="addableOptions" @set-option="addNewOption" />
      </template>
    </template>
  </InfoModal>
</template>
<style lang="scss" scoped>
.modal {
  &__icon {
    display: grid;
    justify-content: center;
  }

  &__container {
    display: grid;
    width: 100%;

    padding: 16px;
    border-radius: 8px;
    column-gap: 8px;
    row-gap: 16px;

    background-color: $backgroundDefault;

    &--base-data {
      grid-template-columns: repeat(20, 1fr);
    }

    &--amount {
      grid-template-columns: repeat(7, 1fr);
    }

    &--order {
      grid-template-columns: repeat(5, 1fr) 120px;
    }
  }

  &__item {
    display: grid;
    grid-template-columns: 100%;
    height: fit-content;

    box-sizing: border-box;
    border-bottom: solid 1px $borderDefault;

    &--base-data {
      &--normal {
        grid-column: span 4;
      }
      &--number {
        grid-column: span 2;
      }
    }
  }
}
</style>
